import { SVGAssets } from '@business/assets/vectors';
import React, { useState, useCallback, useRef, useEffect, FC } from 'react';
import { useDropzone } from 'react-dropzone';
import { validateImageFile } from '@utils/helpers/s3Upload';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  getHaveSubscription,
  getModuleForUpload,
  setUploadFilesSize
} from '@ports/common/sessionStateSlice';
import { generateToken } from '@pushNotifications/firebase';
import { useFirebaseTokenRegister } from '@hooks/useFirebaseTokenRegister';
import { useAvailableStorage } from '@hooks/useAvailableStorage';
import { StorageInformation } from '@business/workflows/ComponentUI/Storage/StorageInformation';
import { SelectGalleryModal } from '@business/workflows/ComponentUI/SelectGalleryModal';
import { Button } from '../Button';
import { showToast } from '../Toast/Toast';
import { UploadErrorBlock } from './UploadErrorBlock';

interface UploadPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload?: (files: File[]) => void;
  onGallerySelect?: (assetIds: string[]) => void;
  isGallerySelectEnabled?: boolean;
  // isPdfAllowed?: boolean;
}

// Extend File to include preview property
type FileWithPreview = File & { preview: string };

type GalleryPreviewItem = { id: string; url: string; type?: string };

export const FilesUploadModal: FC<UploadPhotoModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  onGallerySelect,
  isGallerySelectEnabled = false
  // isPdfAllowed
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { userDeviceHandler } = useFirebaseTokenRegister();
  const {
    availableStorageLabel,
    usedStorageLabel,
    totalStorageLabel,
    isLoading: isStorageLoading,
    isError: isStorageError,
    validateSpace
  } = useAvailableStorage();

  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [galleryItems, setGalleryItems] = useState<GalleryPreviewItem[]>([]);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [errorFiles, setErrorFiles] = useState<File[]>([]); // To track files that failed validation
  const [isSpaceExceeded, setIsSpaceExceeded] = useState<boolean>(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const moduleForUpload = useSelector(getModuleForUpload);
  const isHaveSubscription = useSelector(getHaveSubscription);

  const isAcceptAll =
    moduleForUpload === 'photos' || moduleForUpload === 'group';
  const isEnableGallerySelect =
    (moduleForUpload === 'group' ||
      moduleForUpload === 'moments' ||
      moduleForUpload === 'peopleDetail' ||
      isGallerySelectEnabled) &&
    isHaveSubscription;

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setError('');

    // Validate and add all files
    const validFiles: FileWithPreview[] = [];
    acceptedFiles.forEach(file => {
      const validation = validateImageFile(file); // 50MB limit for videos
      if (!validation.valid) {
        setError(validation.error || 'Invalid file');
        setErrorFiles(prev => [...prev, file]);
      } else {
        const fileWithPreview = Object.assign(file, {
          preview: file.type.startsWith('video/')
            ? URL.createObjectURL(file)
            : URL.createObjectURL(file)
        }) as FileWithPreview;
        validFiles.push(fileWithPreview);
      }
    });

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: isAcceptAll
      ? undefined
      : {
          'image/*': [],
          'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm']
        },
    multiple: true // Change to true for multiple files
  });

  const handleRemoveFile = (fileName: string) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.name === fileName);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.name !== fileName);
    });
  };

  const handleGallerySelect = (
    selectedItems: Array<{ id: string; url: string; type?: string }>
  ) => {
    setGalleryItems(prev => {
      const existingIds = new Set(prev.map(item => item.id));
      const newItems = selectedItems.filter(item => !existingIds.has(item.id));
      return [...prev, ...newItems];
    });
  };

  const handleRemoveGalleryItem = (id: string) => {
    setGalleryItems(prev => prev.filter(item => item.id !== id));
  };

  const handleUpload = async () => {
    if (files.length === 0 && galleryItems.length === 0) {
      setError('Please select at least one file');
      return;
    }

    const uploadFilesTotalSize = files.reduce(
      (sum, file) => sum + (file.size || 0),
      0
    );
    dispatch(
      setUploadFilesSize(
        files.length > 0 ? uploadFilesTotalSize.toString() : '0'
      )
    );

    if (files.length > 0) {
      if (!validateSpace) {
        setError('Storage validation is unavailable. Please try again later.');
        return;
      }

      const validationResult = await validateSpace(files);
      if (!validationResult.ok) {
        if (validationResult.error) {
          setIsSpaceExceeded(false);
          setError('Unable to verify available storage. Please try again.');
        } else {
          setIsSpaceExceeded(true);
          showToast({
            type: 'error',
            isAlert: true,
            alertProps: {
              type: 'error',
              message: t(
                'recipes.notEnoughStorage',
                'You’ve reached your storage limit. To get more storage, please upgrade your plan'
              )
            }
          });
        }
        return;
      }

      generateToken()
        .then(token => {
          if (token) {
            userDeviceHandler(token);
          }
        })
        .catch(err => {
          console.error('Error generating Firebase token:', err);
        });

      onUpload?.(files);
    }

    if (galleryItems.length > 0) {
      onGallerySelect?.(galleryItems.map(item => item.id));
    }

    setFiles([]);
    setGalleryItems([]);
    setError('');
    setErrorFiles([]);
    setIsSpaceExceeded(false);
    onClose();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      // Clear files when modal is closed
      setFiles([]);
      setGalleryItems([]);
      setError('');
      setErrorFiles([]);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup previews
      files.forEach(file => URL.revokeObjectURL(file.preview));
    };
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70]">
        <div
          ref={modalRef}
          className="bg-white rounded-2xl p-6 shadow-lg relative w-[480px] max-h-[90vh] overflow-y-auto">
          <Button
            btnStyle="link"
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl disabled:opacity-50"
            onClick={() => {
              setFiles([]);
              setGalleryItems([]);
              setError('');
              setErrorFiles([]);
              onClose();
            }}>
            <SVGAssets.CloseIconGray className="w-3 h-3" />
          </Button>

          <h3 className="text-lg font-semibold text-[#353535] mb-2">
            {t('dashboard.uploadFiles')}
          </h3>
          <StorageInformation
            isOpen={isSpaceExceeded}
            isStorageError={isStorageError}
            isStorageLoading={isStorageLoading}
            availableStorageLabel={availableStorageLabel}
            usedStorageLabel={usedStorageLabel}
            totalStorageLabel={totalStorageLabel}
          />

          {/* Error Message */}
          {error && <UploadErrorBlock error={error} errorFiles={errorFiles} />}

          {/* Upload Section */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
    max-h-[300px] overflow-y-auto
    ${isDragActive ? 'border-green-500 bg-green-50' : 'border-gray-300'}
  `}>
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center text-[#353535]">
              {files.length === 0 && galleryItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="flex items-center justify-center mb-3">
                    <SVGAssets.ModalUploadIcon className="w-14 h-14" />
                  </div>
                  <p className="font-medium">{t('Upload your files here')}</p>
                  <p className="text-sm text-gray-500">
                    {t('Drag images, videos, audio, or documents here or')}
                  </p>
                </div>
              ) : (
                <div className="mt-4 w-full">
                  <div className="grid grid-cols-3 gap-3">
                    {galleryItems.map(item => (
                      <div key={`gallery-${item.id}`} className="relative">
                        {item.type === 'audio' ? (
                          <div className="flex items-center justify-center h-24 w-24 bg-gray-100 rounded-lg">
                            <SVGAssets.Audio className="w-12 h-12 text-gray-400" />
                          </div>
                        ) : item.type === 'video' ||
                          item.type?.startsWith('video/') ? (
                          <div className="relative h-24 w-24">
                            <img
                              src={item.url}
                              alt="preview"
                              className="rounded-lg h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="bg-black/50 rounded-full p-1.5 backdrop-blur-[1px]">
                                <SVGAssets.PlayButton />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <img
                            src={item.url}
                            alt="preview"
                            className="rounded-lg h-24 w-24 object-cover"
                          />
                        )}
                        <Button
                          btnStyle="link"
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            handleRemoveGalleryItem(item.id);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600">
                          <SVGAssets.CloseIconGray className="w-3 h-3 white-close" />
                        </Button>
                      </div>
                    ))}
                    {files.slice(0, 25).map(file => (
                      <div key={file.name} className="relative">
                        {file.type?.startsWith('video/') ? (
                          <div className="relative h-24 w-24">
                            <video
                              src={file.preview}
                              className="rounded-lg h-full w-full object-cover bg-black"
                              muted
                              preload="metadata"
                              onLoadedMetadata={e => {
                                e.currentTarget.currentTime = 1.0;
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="bg-black/50 rounded-full p-1.5 backdrop-blur-[1px]">
                                <SVGAssets.PlayButton />
                              </div>
                            </div>
                          </div>
                        ) : file.type?.startsWith('image/') ? (
                          <img
                            src={file.preview}
                            alt="preview"
                            className="rounded-lg h-24 w-24 object-cover"
                          />
                        ) : file.type?.startsWith('audio/') ? (
                          <div className="flex items-center justify-center h-24 w-24 bg-gray-100 rounded-lg">
                            <SVGAssets.Audio className="w-12 h-12 text-gray-400" />
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-24 w-24 bg-gray-100 rounded-lg">
                            <SVGAssets.DocIcon className="w-12 h-12 text-gray-400" />
                          </div>
                        )}
                        <Button
                          btnStyle="link"
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            handleRemoveFile(file.name);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600">
                          <SVGAssets.CloseIconGray className="w-3 h-3 white-close" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col items-center gap-1.5 mt-1">
                <div className="flex items-center gap-2">
                  <SVGAssets.AddRoundIcon className="w-4 h-4" />
                  <p className="text-[#7F9D48] font-medium underline whitespace-nowrap">
                    {t('Browse files to upload')}
                  </p>
                </div>
                {isEnableGallerySelect && (
                  <Button
                    btnStyle="link"
                    type="button"
                    startIcon={
                      <SVGAssets.SelectFromGallery className="w-4 h-4 mr-2" />
                    }
                    onClick={e => {
                      e.stopPropagation();
                      setIsGalleryModalOpen(true);
                    }}
                    className="flex items-center text-[#7F9D48] font-medium underline whitespace-nowrap">
                    {t('selectFromGallery')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-between mt-6">
            <Button
              onClick={() => {
                setFiles([]);
                setGalleryItems([]);
                setError('');
                setErrorFiles([]);
                onClose();
              }}
              className="cancel-btn hover:text-white">
              {t('cancel')}
            </Button>
            <Button
              btnStyle="primary"
              onClick={handleUpload}
              disabled={files.length === 0 && galleryItems.length === 0}
              className="bg-[#7F9D48] text-white font-medium px-5 py-2 rounded-md hover:bg-[#6d8c3e] transition disabled:opacity-50 disabled:cursor-not-allowed">
              {t('upload')}
            </Button>
          </div>
        </div>
      </div>

      <SelectGalleryModal
        isOpen={isGalleryModalOpen}
        onClose={() => setIsGalleryModalOpen(false)}
        onSelect={handleGallerySelect}
        multiSelect
      />
    </>
  );
};
